import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import axios, { AxiosError } from 'axios';
import produce from 'immer';
import _ from 'lodash';

import {
  FILE_NAME_TYPE,
  INFO_ITEM_TYPE,
  ITEM_INFO_TYPES,
  POI_TYPE,
  SCENARIO_ACTION_TYPE,
} from '../../../commons/constants/constants';
import AimPoi from '../../../model/Poi/marker/AimPoi';
import PoiItemInfo from '../../../model/Poi/PoiItemInfo';
import PoiPhoto from '../../../model/Poi/PoiPhoto';
import PoiPolygon from '../../../model/Poi/PoiPolygon';
import PoiVideo from '../../../model/Poi/PoiVideo';
import { getFlareConfig } from '../../../services/flare.service';
import {
  cloneTourScenario,
  enableTourScenario,
  getTourByCode,
  saveConfigTour,
  saveTourContacts,
  updateBlendMode,
  updateDefaultTourScenario,
  updateHotspotsAGTConfig,
  updateProductQuotations,
  updateTourBackgroundSound,
  updateTourMask,
  updateTourScenario,
  updateTourSceneName,
  updateTourScenes,
  updateTourSetting,
} from '../../../services/tour.service';
import { uploadFiles } from '../../../services/uploader.service';
import { BrandInfoData, LocationInfoData, ProductInfoData } from '../../../types/ItemInfoData';
import { PoiData, PoiItemInfoData } from '../../../types/PoiData';
import { ProductCollectionData } from '../../../types/ProductCollectionData';
import { SceneData } from '../../../types/SceneData';
import { TourData, TourProductQuotationData } from '../../../types/TourData';
import { nonNull } from '../../../utils/commonUtils';
import { showMessage } from '../../../utils/messageError';
import { storeName as tourGuideEntityStore } from '../../entities/tourGuide.entity';
import { RootModel } from '../../models';
import { storeName as categoryStore } from '../category.model';
import { storeName as themeStore } from '../theme/theme.model';
import { storeName as userStore } from '../user.model';
import { storeName as configModalStore } from './configModal.model';

const getQuotationKey = (quo: TourProductQuotationData): string => {
  return `${quo.scene ? 'scn' : 'cate'}-${quo.scene?.id || quo.category?.id}-${quo.product.id}-${
    quo.tour.id
  }`;
};

export const storeName = 'tourDetail';

const initialValues = {
  data: null,
  metadata: {
    flare: {},
  },
  pois: {},
  poiMarkers: null,
  startedMarker: false,
  loading: false,
  visibleTourScenario: false,
  visibleTourScenarioStepActions: null,
  visibleTourScenarioStepScene: null,
  poisKeyBelongToAGTAction: [], // key = AGT tempId, and poi code
  quotations: {},
  tourReady: false,
};

export const tourDetail: any = createModel<RootModel>()({
  state: initialValues, // initial state
  reducers: {
    setData: produce((state, payload) => {
      const { data, metadata } = payload;
      state.data = data;
      state.metadata = metadata;
    }),
    setPois: produce((state, payload) => {
      state.pois = payload || [];
    }),
    setPoiMarkers: produce((state, payload) => {
      state.poiMarkers = payload;
    }),
    setStartedMarker: produce((state, payload) => {
      state.startedMarker = payload;
    }),
    setTourReady: produce((state, payload) => {
      state.tourReady = payload;
    }),
    updatePoiMarkerPosition: produce((state, payload) => {
      state.poiMarkers = state.poiMarkers.map((p: AimPoi) => {
        if (p.code === payload.code) {
          const { ath, atv } = payload;
          return { ...p, ath, atv };
        }

        return p;
      });
    }),
    setSelectedPoiMarkers: produce((state, payload) => {
      state.poiMarkers = state.poiMarkers.map((p: AimPoi) => {
        if (p.code === payload.code) {
          return { ...p, selected: true };
        }

        return { ...p, selected: false };
      });
    }),
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setVisibleTourScenario: produce((state, payload) => {
      state.visibleTourScenario = payload;
    }),
    setVisibleTourScenarioStepActions: produce((state, payload) => {
      state.visibleTourScenarioStepActions = payload;
    }),
    setPoisKeyBelongToAGTAction: produce((state, payload) => {
      state.poisKeyBelongToAGTAction = payload;
    }),
    setQuotations: produce((state, payload) => {
      state.quotations = payload || {};
    }),
    setVisibleTourScenarioStepScene: produce((state, payload) => {
      state.visibleTourScenarioStepScene = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof tourDetail>) => ({
    async getTour(payload, state) {
      const { pois = {} } = state[storeName];
      const { data } = await getTourByCode(payload);
      const tourData = data as TourData;

      const poisByScenes: { [key: string]: PoiData[] } = {};
      Object.values(pois).forEach((poi: PoiData & { sceneId: number }) => {
        if (!poisByScenes[`${poi.sceneId}`]) {
          poisByScenes[`${poi.sceneId}`] = [];
        }

        poisByScenes[`${poi.sceneId}`].push(poi);
      });

      tourData.scenes.forEach((sceneData) => {
        const tempPois = poisByScenes[`${sceneData.id}`];
        if (!tempPois) {
          return;
        }

        const newPois: PoiData[] = [];
        tempPois.forEach((poiData) => {
          if (!poiData.id) {
            newPois.push(poiData);
            return;
          }

          const poi = sceneData.pois.find((p) => p.id === poiData.id);
          Object.assign(poi, {
            ...poi,
            ...poiData,
          });
        });

        sceneData.pois.push(...newPois);
      });

      let metadata = null;
      try {
        const flareConfig = await getFlareConfig();
        metadata = flareConfig.data;
      } catch (e) {
        console.error(e);
      }

      const tourScenarios = tourData.tourScenarios || null;
      if (tourScenarios && tourScenarios.length) {
        const tourGuides = [];
        tourScenarios.forEach((tourScenario) => {
          const steps = tourScenario.steps || [];
          steps.forEach((s) => {
            const actions = s.actions || [];
            const children = s.children || [];
            children.forEach((c) => actions.push(...c.actions));
            actions.forEach((a) => {
              const { type } = a;
              if (type === SCENARIO_ACTION_TYPE.PLAY_SOUND) {
                const { soundEN, soundVN } = a;
                if (soundEN) {
                  tourGuides.push(soundEN);
                }
                if (soundVN) {
                  tourGuides.push(soundVN);
                }
              }
            });
          });
        });
        dispatch[tourGuideEntityStore].saves(tourGuides);
      }

      await this.setData({ data: tourData, metadata });
      dispatch[themeStore].setTourTheme(tourData.theme || null);

      const scenes = {};
      tourData.scenes.forEach((sceneData) => {
        scenes[`${sceneData.id}`] = [...sceneData.pois];
      });
      this.getQuotations({ scenes });
    },
    async saveConfigTour(payload, state) {
      this.setLoading(true);
      try {
        const { user } = state[userStore];
        const data = _.cloneDeep(payload);
        const { scenes = [] } = data;
        const uploadActions: any[] = [];
        const pois = scenes.flatMap((sc: any) => sc.pois);
        const soundPois = pois.filter((poi: any) => poi.type === POI_TYPE.SOUND);
        soundPois.forEach((poi: any) => {
          const { sound } = poi;
          if (sound) {
            const promise = uploadFiles(
              poi.id,
              [sound],
              FILE_NAME_TYPE.AUDIO,
              user.id,
              'sounds'
            ).then((result) => {
              const { filesList } = result;
              if (filesList.length) {
                const firstFile = filesList[0];
                poi.sound = { id: firstFile.id, url: firstFile.url };
              }
            });
            uploadActions.push(promise);
          }
        });

        const popupPois = pois.filter((poi: any) => poi.type === POI_TYPE.POPUP);
        popupPois.forEach((poi: any) => {
          const { image } = poi;
          if (image) {
            const promise = uploadFiles(
              poi.id,
              [image],
              FILE_NAME_TYPE.IMAGE,
              user.id,
              'popup/image'
            ).then((result) => {
              const { filesList } = result;
              if (filesList.length) {
                const firstFile = filesList[0];
                poi.image = { id: firstFile.id, url: firstFile.url };
              }
            });
            uploadActions.push(promise);
          }
        });

        const photoPois: PoiPhoto[] = pois.filter((poi: any) => poi.type === POI_TYPE.PHOTO);
        photoPois.forEach((poi: PoiPhoto) => {
          const { image } = poi;
          if (image) {
            const promise = uploadFiles(
              poi.id,
              [image],
              FILE_NAME_TYPE.IMAGE,
              user.id,
              'photos'
            ).then((result) => {
              const { filesList } = result;
              if (filesList.length) {
                const firstFile = filesList[0];
                poi.image = { id: firstFile.id, url: firstFile.url };
              }
            });
            uploadActions.push(promise);
          }
        });

        const videoPois: PoiVideo[] = pois.filter((poi: any) => poi.type === POI_TYPE.VIDEO);
        videoPois.forEach((poi: PoiVideo) => {
          const { image, video } = poi;
          if (image) {
            const promiseImage = uploadFiles(
              poi.id,
              [image],
              FILE_NAME_TYPE.IMAGE,
              user.id,
              'videos'
            ).then((result) => {
              const { filesList } = result;
              if (filesList.length) {
                const firstFile = filesList[0];
                poi.image = { id: firstFile.id, url: firstFile.url };
              }
            });
            uploadActions.push(promiseImage);
          }
          if (video) {
            const promiseVideo = uploadFiles(
              poi.id,
              [video],
              FILE_NAME_TYPE.VIDEO,
              user.id,
              'videos'
            ).then((result) => {
              const { filesList } = result;
              if (filesList.length) {
                const firstFile = filesList[0];
                poi.video = { id: firstFile.id, url: firstFile.url };
              }
            });
            uploadActions.push(promiseVideo);
          }
        });

        const itemPois: (PoiItemInfo | PoiPolygon)[] = pois.filter(
          (poi: any) => poi.type === POI_TYPE.ITEM_INFO || poi.type === POI_TYPE.POLYGON
        );
        const uploadCollectionActions: any[] = [];
        itemPois.forEach((poi: PoiItemInfo) => {
          const { itemInfo } = poi;
          if (itemInfo) {
            const { type, icon } = itemInfo;
            if (icon) {
              const promiseFile = uploadFiles(
                poi.id,
                [icon],
                FILE_NAME_TYPE.ITEM_INFO.ICON,
                user.id,
                'info/audio'
              ).then((result) => {
                const { filesList } = result;
                if (filesList.length) {
                  const firstFile = filesList[0];
                  poi.itemInfo.icon = { id: firstFile.id, url: firstFile.url };
                }
              });
              uploadActions.push(promiseFile);
            }
            switch (type) {
              case INFO_ITEM_TYPE.PRODUCT:
                const { collections } = itemInfo as ProductInfoData;
                if (collections && !!collections.length) {
                  collections.forEach((collection: ProductCollectionData) => {
                    if (collection.icon) {
                      const promiseFile = uploadFiles(
                        poi.id,
                        [collection.icon],
                        FILE_NAME_TYPE.ITEM_INFO.COLLECTION_ICON,
                        user.id,
                        'info/collection/icon'
                      ).then((result) => {
                        const { filesList } = result;
                        if (filesList.length) {
                          const firstFile = filesList[0];
                          collection.icon = { id: firstFile.id, url: firstFile.url };
                        }
                      });
                      uploadCollectionActions.push(promiseFile);
                    }
                    if (collection.gallery && !!collection.gallery.length) {
                      const promiseFile = uploadFiles(
                        poi.id,
                        collection.gallery,
                        FILE_NAME_TYPE.ITEM_INFO.CATEGORY_GALLERY,
                        user.id,
                        'info/collection/gallery'
                      ).then((result) => {
                        const { filesList = [] } = result;
                        if (filesList.length) {
                          collection.gallery = filesList.map((file) => {
                            return { id: file.id, url: file.url };
                          });
                        }
                      });
                      uploadCollectionActions.push(promiseFile);
                    }
                  });
                }
                break;
              case INFO_ITEM_TYPE.LOCATION:
                const { audioFile } = itemInfo as LocationInfoData;
                if (audioFile) {
                  const promiseFile = uploadFiles(
                    poi.id,
                    [audioFile],
                    FILE_NAME_TYPE.ITEM_INFO.AUDIO,
                    user.id,
                    'info/audio'
                  ).then((result) => {
                    const { filesList } = result;
                    if (filesList.length) {
                      const firstFile = filesList[0];
                      (poi.itemInfo as LocationInfoData).audioFile = {
                        id: firstFile.id,
                        url: firstFile.url,
                      };
                    }
                  });
                  uploadActions.push(promiseFile);
                }
                break;
              case INFO_ITEM_TYPE.BRAND:
                const { logo } = itemInfo as BrandInfoData;
                if (logo) {
                  const promiseFile = uploadFiles(
                    poi.id,
                    [logo],
                    FILE_NAME_TYPE.ITEM_INFO.LOGO,
                    user.id,
                    'info/audio'
                  ).then((result) => {
                    const { filesList } = result;
                    if (filesList.length) {
                      const firstFile = filesList[0];
                      (poi.itemInfo as BrandInfoData).logo = {
                        id: firstFile.id,
                        url: firstFile.url,
                      };
                    }
                  });
                  uploadActions.push(promiseFile);
                }
                break;
              default:
                console.log('unknow type: ', type);
            }
          }
        });

        await axios.all(uploadActions);
        await axios.all(uploadCollectionActions);
        await saveConfigTour(data.id, data);
        message.success('Success to save tour');
        this.setPois({});
        this.getTour(state[storeName].data.code);
        this.setPoisKeyBelongToAGTAction([]);
      } catch (e: unknown) {
        const axiosError = e as AxiosError;
        showMessage(axiosError, 'Fail to save tour');
        throw axiosError;
      } finally {
        this.setLoading(false);
      }
    },
    async saveTourContacts(payload = {}, state) {
      this.setLoading(true);
      try {
        const { id: tourId, configLastModifiedDate } = state[storeName].data;
        await saveTourContacts(tourId, { ...payload, configLastModifiedDate });
        message.success(`Save tour contact successfully`);
        this.getTour(state[storeName].data.code);
      } catch (err: unknown) {
        const axiosError = err as AxiosError;
        console.error(axiosError.message);
        showMessage(axiosError, `Fail to update tour contact: ${axiosError.message}`);
      } finally {
        this.setLoading(false);
      }
    },
    async updateTourStartScene(payload, state) {
      this.setLoading(true);
      try {
        const { startSceneCode, data } = payload;
        const tourId = state[storeName].data.id;
        dispatch[configModalStore].setStartingScene(startSceneCode);
        const { data: respData } = await updateTourScenes(tourId, data);
        if (respData.scenes) {
          dispatch[categoryStore].getCategories(respData.scenes);
          message.success(`Update start scene successfully`);
        }
      } catch (err) {
        message.error(`Fail to set start scene : ${err.message}`);
        console.error(err.message);
      } finally {
        this.setLoading(false);
      }
    },
    async updateTourSceneViewConfig(payload, state) {
      this.setLoading(true);
      try {
        const { id: tourId, code: tourCode, lastModifiedDate } = state[storeName].data;
        const { user } = state[userStore];
        const uploadActions: Promise<void>[] = [];
        const { scenes = [], applyAll = false } = payload;

        if (!scenes.length) {
          return;
        }

        if (applyAll) {
          const [scene] = scenes;
          const nadirPoi = scene.pois.find((poi) => !poi.zenith);
          if (nadirPoi && nadirPoi.image) {
            const promiseFile = uploadFiles(
              tourId,
              [nadirPoi.image],
              FILE_NAME_TYPE.ITEM_INFO.LOGO,
              user.id,
              `nadir`
            ).then((result) => {
              const { filesList } = result;
              if (filesList.length) {
                const firstFile = filesList[0];
                scenes.forEach((cfg) => {
                  cfg.pois.forEach((poi) => {
                    if (!poi.zenith) {
                      poi.image = { id: firstFile.id, url: firstFile.url };
                    }
                  });
                });
              }
            });
            uploadActions.push(promiseFile);
          }

          const zenithPoi = scene.pois.find((poi) => !!poi.zenith);
          if (zenithPoi && zenithPoi.image) {
            const promiseFile = uploadFiles(
              tourId,
              [zenithPoi.image],
              FILE_NAME_TYPE.ITEM_INFO.LOGO,
              user.id,
              `nadir`
            ).then((result) => {
              const { filesList } = result;
              if (filesList.length) {
                const firstFile = filesList[0];
                scenes.forEach((cfg) => {
                  cfg.pois.forEach((poi) => {
                    if (poi.zenith) {
                      poi.image = { id: firstFile.id, url: firstFile.url };
                    }
                  });
                });
              }
            });
            uploadActions.push(promiseFile);
          }
        } else {
          scenes.forEach((cfg) => {
            (cfg.pois || []).forEach((poi) => {
              const { image } = poi;
              if (image) {
                const promise = uploadFiles(
                  poi.id,
                  [image],
                  FILE_NAME_TYPE.ITEM_INFO.LOGO,
                  user.id,
                  'nadir'
                ).then((result) => {
                  const { filesList } = result;
                  if (filesList.length) {
                    const firstFile = filesList[0];
                    poi.image = { id: firstFile.id, url: firstFile.url };
                  }
                });
                uploadActions.push(promise);
              }
            });
          });
        }
        await Promise.all(uploadActions);
        await updateTourMask(tourId, { id: tourId, scenes, lastModifiedDate });
        message.success('Update view configs successfully');
        await this.getTour(tourCode);
      } catch (err: unknown) {
        const axiosError = err as AxiosError;
        console.error(axiosError.message);
        showMessage(axiosError, `Fail to update view configs: ${axiosError.message}`);
      } finally {
        this.setLoading(false);
      }
    },
    async updatePoi(payload, state) {
      if (payload.type === POI_TYPE.NADIR_LOGO) {
        return;
      }

      const { pois = {} } = state[storeName];
      const { activeScene } = state[configModalStore];
      const newPois = { ...pois };
      newPois[payload.code] = {
        ...payload,
        sceneId: activeScene.id,
      };
      if (payload.removed && !payload.id) {
        delete newPois[payload.code];
      }

      this.setPois(newPois);
      this.getQuotations({ pois: newPois });
    },
    async updateTourSetting(payload, state) {
      this.setLoading(true);
      try {
        const { id: tourId, code: tourCode, configLastModifiedDate } = state[storeName].data;
        await updateTourSetting(tourId, { ...payload, configLastModifiedDate });
        message.success(`Update tour config successfully`);
        this.getTour(tourCode);
      } catch (err: unknown) {
        const axiosError = err as AxiosError;
        console.error(axiosError.message);
        showMessage(axiosError, `Fail to update tour config: ${axiosError.message}`);
      } finally {
        this.setLoading(false);
      }
    },
    async updateBackgroundSound(payload, state) {
      this.setLoading(true);
      try {
        const { id: tourId, code: tourCode, configLastModifiedDate } = state[storeName].data;
        await updateTourBackgroundSound(tourId, { ...payload, configLastModifiedDate });
        message.success(`Update tour background sound successfully`);
        this.getTour(tourCode);
      } catch (err: unknown) {
        const axiosError = err as AxiosError;
        console.error(axiosError.message);
        showMessage(axiosError, 'Fail to update tour background sound');
      } finally {
        this.setLoading(false);
      }
    },
    async updateTourScenario(payload, state) {
      this.setLoading(true);
      try {
        const { tourData, tourScenarios } = payload;
        const { id: tourId, code: tourCode } = state[storeName].data;
        await this.saveConfigTour(tourData);
        await updateTourScenario(tourId, tourScenarios);
        message.success(`Update tour scenario successfully`);
        this.getTour(tourCode);
      } catch (err: unknown) {
        const axiosError = err as AxiosError;
        showMessage(axiosError, `Fail to update tour scenario: ${axiosError.message}`);
      } finally {
        this.setLoading(false);
      }
    },
    async updateTourBlendMode(payload, state) {
      this.setLoading(true);
      try {
        const { id: tourId, code: tourCode, configLastModifiedDate } = state[storeName].data;
        await updateBlendMode(tourId, { ...payload, configLastModifiedDate });
        this.getTour(tourCode);
        message.success('Update tour config successfully');
      } catch (err) {
        const axiosError = err as AxiosError;
        showMessage(axiosError, `Fail to update blend mode: ${err.message}`);
      } finally {
        this.setLoading(false);
      }
    },
    async updateTourSceneName(payload, state) {
      try {
        this.setLoading(true);
        const { id: tourId, code: tourCode } = state[storeName].data;
        await updateTourSceneName(tourId, payload);
        this.getTour(tourCode);
        message.success('Update tour config successfully');
      } catch (err) {
        message.error(`Fail to update tour scene name: ${err.message}`);
        console.error(err);
      } finally {
        this.setLoading(false);
      }
    },
    async enableTourScenario(payload, state) {
      this.setLoading(true);
      try {
        const { id: tourId, code: tourCode } = state[storeName].data;
        const { tourScenarioId, enabled } = payload;

        await enableTourScenario(tourId, tourScenarioId, enabled);
        message.success(`${enabled ? 'Enable' : 'Disable'} tour scenario successfully`);
        this.getTour(tourCode);
      } catch (err) {
        message.error(
          `Failed ${payload.enabled ? 'enable' : 'disable'} tour scenario: ${err.message}`
        );
        console.error(err.message);
      } finally {
        this.setLoading(false);
      }
    },
    async updateDefaultTourScenario(payload, state) {
      this.setLoading(true);
      try {
        const { id: tourId, code: tourCode } = state[storeName].data;
        const { tourScenarioId, isDefault } = payload;

        await updateDefaultTourScenario(tourId, tourScenarioId, isDefault);
        message.success(`${isDefault ? 'Set' : 'Unset'} default tour scenario successfully`);
        this.getTour(tourCode);
      } catch (err) {
        message.error(
          `Failed ${payload.isDefault ? 'set' : 'unset'} default tour scenario: ${err.message}`
        );
        console.error(err.message);
      } finally {
        this.setLoading(false);
      }
    },
    async cloneTourScenario(payload, state) {
      this.setLoading(true);
      try {
        const { tourData, tourScenarioIds } = payload;
        await this.saveConfigTour(tourData);

        const { id: tourId, code: tourCode } = state[storeName].data;
        await cloneTourScenario(tourId, { tourScenarioIds, tourId });
        message.success(`Clone tour scenario successfully`);
        this.getTour(tourCode);
      } catch (err) {
        message.error(`Fail to clone tour scenario: ${err.message}`);
        console.error(err.message);
      } finally {
        this.setLoading(false);
      }
    },
    async updateHotspotsAGTConfig(payload, state) {
      this.setLoading(true);
      try {
        const { id: tourId } = state.tourDetail.data;
        const res = await updateHotspotsAGTConfig(tourId, payload);
        message.success(`Update hotspots AGT config successfully`);
        return res;
      } catch (err) {
        message.error(`Fail to update hotspots AGT config: ${err.message}`);
        console.error(err.message);
      } finally {
        this.setLoading(false);
      }
    },
    async getQuotations(payload, state) {
      const { quotations: storedQuotationMap } = state[storeName];
      const { tour, pois = {} } = payload;

      let tourData: TourData = tour;
      if (!tourData) {
        tourData = state[storeName].data;
      }

      let poiScenes = payload.scenes;
      if (!poiScenes) {
        poiScenes = this.getPois({ data: tourData, pois });
      }

      const { scenes, quotations: tourQuotations = [] } = tourData;
      const savedQuotationMap = (tourQuotations as TourProductQuotationData[]).reduce(
        (prev, curr) => {
          prev[getQuotationKey(curr)] = curr;
          return prev;
        },
        {}
      );

      const categoryScenes: { [key: string]: SceneData[] } = {};
      const otherScenes: SceneData[] = [];

      scenes.forEach((scn: SceneData) => {
        if (!scn.category) {
          otherScenes.push(scn);
        } else {
          if (!categoryScenes[`${scn.category.id}`]) {
            categoryScenes[`${scn.category.id}`] = [];
          }

          categoryScenes[`${scn.category.id}`].push(scn);
        }
      });

      const quotationMap = {};
      otherScenes.forEach((scn) => {
        (poiScenes[scn.id] || []).forEach((p) => {
          if (p.removed) {
            return;
          }

          if (p.type !== POI_TYPE.ITEM_INFO && p.type !== POI_TYPE.POLYGON) {
            return;
          }

          const poiItemInfo = p as PoiItemInfoData;
          const { itemInfo } = poiItemInfo;

          if (
            !itemInfo ||
            (itemInfo.type !== ITEM_INFO_TYPES.PRODUCT &&
              itemInfo.type !== ITEM_INFO_TYPES.ECOMMERCE_PRODUCT)
          ) {
            return;
          }

          const productId = itemInfo.id;
          quotationMap[`scn-${scn.id}-${productId}-${tourData.id}`] = {
            tour: { id: tourData.id },
            scene: { id: scn.id },
            product: { id: productId },
            price: null,
          };
        });
      });

      Object.entries(categoryScenes).forEach(([key, scns]) => {
        const categoryId = Number(key);
        scns.forEach((scn) => {
          (poiScenes[scn.id] || []).forEach((p) => {
            if (p.removed) {
              return;
            }

            if (p.type !== POI_TYPE.ITEM_INFO && p.type !== POI_TYPE.POLYGON) {
              return;
            }

            const poiItemInfo = p as PoiItemInfoData;
            const { itemInfo } = poiItemInfo;

            if (
              !itemInfo ||
              (itemInfo.type !== ITEM_INFO_TYPES.PRODUCT &&
                itemInfo.type !== ITEM_INFO_TYPES.ECOMMERCE_PRODUCT)
            ) {
              return;
            }

            const productId = itemInfo.id;
            quotationMap[`cate-${categoryId}-${productId}-${tourData.id}`] = {
              tour: { id: tourData.id },
              category: { id: categoryId },
              product: { id: productId },
              price: null,
            };
          });
        });
      });

      Object.keys(quotationMap).forEach((key) => {
        const saved = savedQuotationMap[key] || {};
        const stored = storedQuotationMap[key] || {};
        const temp = quotationMap[key];
        quotationMap[key] = {
          ...temp,
          ...saved,
          ...stored,
        };
      });

      this.setQuotations(quotationMap);
    },
    async updateQuotations(payload: TourProductQuotationData[] = [], state) {
      try {
        this.setLoading(true);
        const { quotations: storedQuotationMap } = state[storeName];
        const quotations = [...payload].filter((p) => nonNull(p.price));
        quotations.forEach((quo) => {
          const key = getQuotationKey(quo);
          const stored: TourProductQuotationData = storedQuotationMap[key];
          if (stored) {
            quo.id = stored.id;
            quo.code = stored.code;
          }
        });

        const { id: tourId, code } = state[storeName].data;
        await updateProductQuotations(tourId, {
          id: tourId,
          quotations,
        });
        await this.setQuotations({});
        this.getTour(code);
        message.success(`Update product quotations successfully`);
        return true;
      } catch (e) {
        message.error(`Fail to update product quotations`);
        console.error(e);
        return false;
      } finally {
        this.setLoading(false);
      }
    },
    getPois(payload, state) {
      const { data, pois } = payload;

      let tourData = data;
      if (!tourData) {
        ({ data: tourData } = state[storeName]);
      }

      let storedPois = pois;
      if (!storedPois) {
        ({ pois: storedPois } = state[storeName]);
      }

      const poisByScenes: { [key: string]: PoiData[] } = {};
      Object.values(storedPois).forEach((poi: PoiData & { sceneId: number }) => {
        if (!poisByScenes[`${poi.sceneId}`]) {
          poisByScenes[`${poi.sceneId}`] = [];
        }

        poisByScenes[`${poi.sceneId}`].push(poi);
      });

      const scenes = {};
      tourData.scenes.forEach((sceneData) => {
        scenes[`${sceneData.id}`] = sceneData.pois.map((p) => ({
          id: p.id,
          code: p.code,
          type: p.type,
          itemInfo: p.itemInfo,
          sceneId: p.sceneId,
          removed: p.removed,
        }));
        const tempPois = poisByScenes[`${sceneData.id}`];
        if (!tempPois) {
          return;
        }

        const newPois: PoiData[] = [];
        tempPois.forEach((poiData) => {
          if (!poiData.id) {
            newPois.push(poiData);
            return;
          }

          const poi = scenes[`${sceneData.id}`].find((p) => p.id === poiData.id);
          Object.assign(poi, {
            ...poi,
            ...poiData,
          });
        });

        scenes[`${sceneData.id}`].push(...newPois);
      });

      return scenes;
    },
  }),
});

export default tourDetail;
